import { useContext, useEffect, useState } from 'react';
import {
    Box,
    Drawer,
    useMediaQuery,
    useTheme,
    List,
    ListItemButton,
    ListItemText,
    Collapse,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import { Context } from '@/context';
import NavigationItems from '../NavigationItems';
import DrawerMenuRedesSociales from './DrawerMenuRedesSociales';
import useIsUserPremium from '@/DesignSystem/hooks/useIsUserPremium';
import CustomLink from '../../custom/custom-link';
import { Rutas } from '@/constants/rutas';

// Componente para renderizar un ítem del menú
const MenuItem = ({ item, level = 0, handleClose, favoritos, toggleFavorito, isUserPremium }) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const hasSubmenu = item.subMenu && item.subMenu.length > 0;
    const isFavorito = item.path && favoritos.includes(item.path);

    const handleClick = () => {
        if (hasSubmenu) {
            setOpen(!open);
        }
    };

    return (
        <>
            <ListItemButton
                onClick={handleClick}
                sx={{
                    pl: 2 + level * 2,
                    backgroundColor: open ? theme.palette.action.hover : 'transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                    {item.path ? (
                        <CustomLink
                            href={item.path}
                            useNativeLink={!isUserPremium}
                            useWrapper={true}
                            linkStyles={{ width: '100%', height: '100%' }}
                        >
                            <Typography
                                onClick={handleClose}
                                sx={{
                                    textDecoration: 'none',
                                    color: theme.palette.footer.text,
                                    fontWeight: open ? 500 : 400,
                                    fontSize: '1rem',
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                {item.title}
                            </Typography>
                        </CustomLink>
                    ) : (
                        <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{
                                color: theme.palette.footer.text,
                                fontWeight: open ? 600 : 400,
                                fontSize: '1rem',
                            }}
                        />
                    )}
                </Box>

                {!hasSubmenu ? (
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorito(item.path);
                        }}
                        sx={{ ml: 1, display: 'flex', alignItems: 'center' }}
                    >
                        {isFavorito ? (
                            <StarIcon sx={{ color: 'gold' }} />
                        ) : (
                            <StarBorderIcon sx={{ color: theme.palette.footer.text }} />
                        )}
                    </Box>
                ) : (
                    <KeyboardArrowDownIcon
                        sx={{
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                            color: theme.palette.footer.text,
                        }}
                    />
                )}
            </ListItemButton>

            {hasSubmenu && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {item.subMenu.map((subItem, index) => (
                            <MenuItem
                                key={`${item.title}-sub-${index}`}
                                item={subItem}
                                level={level + 1}
                                handleClose={handleClose}
                                favoritos={favoritos}
                                toggleFavorito={toggleFavorito}
                                isUserPremium={isUserPremium}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

// Componente principal del drawer
const DrawerMenuComponent = ({ anchor = 'left', open, handleClose }) => {
    const { state } = useContext(Context);
    const { user } = state;
    const isDesktop = useMediaQuery('(min-width: 992px)');
    const theme = useTheme();
    const isUserPremium = useIsUserPremium();

    const [favoritos, setFavoritos] = useState([]);

    useEffect(() => {
        const stored = localStorage.getItem('drawer_favoritos');
        if (stored) {
            setFavoritos(JSON.parse(stored));
        } else {
            // Si no hay favoritos guardados, establecer favoritos por defecto
            const defaultFavoritos = [
                Rutas.ALINEACIONES_PROBABLES,
                Rutas.MERCADO,
                Rutas.ANALIZA_TU_PLANTILLA,
                Rutas.PREDICCION_DE_MERCADO,
                Rutas.APUESTAS_DEPORTIVAS,
            ];
            setFavoritos(defaultFavoritos);
            localStorage.setItem('drawer_favoritos', JSON.stringify(defaultFavoritos));
        }
    }, []);

    const toggleFavorito = (path) => {
        setFavoritos((prev) => {
            const newList = prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path];
            localStorage.setItem('drawer_favoritos', JSON.stringify(newList));
            return newList;
        });
    };

    const allNavigationItems = NavigationItems(user, isDesktop, 20, true);

    // Filtrar favoritos y eliminar duplicados por path
    const favoritosItems = allNavigationItems
        .flatMap((item) =>
            item.subMenu ? item.subMenu.filter((sub) => favoritos.includes(sub.path)) : []
        )
        .filter((item, index, self) =>
            index === self.findIndex((t) => t.path === item.path)
        );

    const restantes = allNavigationItems
        .map((item) => {
            if (!item.subMenu) return item;

            const filteredSubMenu = item.subMenu.filter((sub) => !favoritos.includes(sub.path));

            return {
                ...item,
                subMenu: filteredSubMenu,
            };
        })
        .filter((item) => {
            // Filtrar padres que se quedaron sin submenús
            return !item.subMenu || item.subMenu.length > 0;
        });

    return (
        <Drawer
            anchor={anchor}
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    backgroundColor: theme.palette.footer.background,
                    width: { xs: '90%', sm: 400 },
                    top: 47,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Box
                component="nav"
                sx={{
                    maxHeight: 'calc(100vh - 47px)',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <DrawerMenuRedesSociales displayToogle={true} />
                <List component="nav">
                    {favoritosItems.map((item) => (
                        <MenuItem
                            key={`fav-${item.path}`}
                            item={item}
                            handleClose={handleClose}
                            favoritos={favoritos}
                            toggleFavorito={toggleFavorito}
                            isUserPremium={isUserPremium}
                        />
                    ))}

                    {favoritosItems.length > 0 && <Box sx={{ height: 8 }} />}

                    {restantes.map((item, index) => (
                        <MenuItem
                            key={item.id || `nav-${index}`}
                            item={item}
                            handleClose={handleClose}
                            favoritos={favoritos}
                            toggleFavorito={toggleFavorito}
                            isUserPremium={isUserPremium}
                        />
                    ))}
                </List>
            </Box>
        </Drawer>
    );
};

export default DrawerMenuComponent;
